import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Search, UserPlus, Edit3, Trash2, KeyRound, Eye, ShieldCheck, FileSpreadsheet, Radio } from "lucide-react";

const empty = { full_name: "", phone: "", email: "", password: "", status: "Active" };

function formatWhen(iso) {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Never";
    return d.toLocaleString();
  } catch { return "Never"; }
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [resetting, setResetting] = useState(null);

  const load = () => api.get("/admin/users", { params: { q } }).then((r) => setUsers(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setCreating(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    const { id, full_name, phone, status } = editing;
    try {
      await api.patch(`/admin/users/${id}`, { full_name, phone, status });
      toast.success("Updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator</div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight">Users</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, phone..."
              data-testid="admin-users-search"
              className="pl-9 bg-zinc-900 border-zinc-800 w-72" />
          </div>
          <Button onClick={() => setCreating(true)} data-testid="add-user-btn"
            className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">
            <UserPlus className="w-4 h-4 mr-2" /> Add New User
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-4">Name</th>
              <th className="p-4">Email</th>
              <th className="p-4">Phone</th>
              <th className="p-4">Status</th>
              <th className="p-4">Password</th>
              <th className="p-4">Last Login</th>
              <th className="p-4 text-right">Songs</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-cyan-400 text-xs font-bold">
                      {u.full_name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div>{u.full_name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
                        {u.role === "admin" ? "Admin" : "Artist"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-zinc-300">{u.email}</td>
                <td className="p-4 text-zinc-300">{u.phone}</td>
                <td className="p-4">
                  <span className={`px-2 py-0.5 rounded-md text-xs border ${(u.status || "Active") === "Active" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-rose-500/10 text-rose-400 border-rose-500/30"}`}>
                    {u.status || "Active"}
                  </span>
                </td>
                <td className="p-4">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" data-testid={`user-password-status-${u.id}`}>
                    <ShieldCheck className="w-3 h-3" /> Set
                  </span>
                </td>
                <td className="p-4 text-zinc-400 text-sm" data-testid={`user-last-login-${u.id}`}>
                  {formatWhen(u.last_login_at)}
                </td>
                <td className="p-4 text-right font-mono text-cyan-400">{u.submission_count}</td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setDetail(u)} data-testid={`view-user-${u.id}`} className="h-8 w-8 text-zinc-400 hover:text-cyan-400" title="View details"><Eye className="w-4 h-4" /></Button>
                    {u.role !== "admin" && (
                      <>
                        <Link to={`/admin/users/${u.id}/bulk-import`}>
                          <Button size="icon" variant="ghost" data-testid={`bulk-import-${u.id}`} className="h-8 w-8 text-zinc-400 hover:text-emerald-400" title="Bulk song import"><FileSpreadsheet className="w-4 h-4" /></Button>
                        </Link>
                        <Link to={`/admin/users/${u.id}/dsp-links`}>
                          <Button size="icon" variant="ghost" data-testid={`dsp-links-${u.id}`} className="h-8 w-8 text-zinc-400 hover:text-cyan-400" title="DSP Links"><Radio className="w-4 h-4" /></Button>
                        </Link>
                        <Button size="icon" variant="ghost" onClick={() => setEditing({ ...u })} data-testid={`edit-user-${u.id}`} className="h-8 w-8 text-zinc-400 hover:text-violet-400" title="Edit user"><Edit3 className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setResetting(u)} data-testid={`reset-password-${u.id}`} className="h-8 w-8 text-zinc-400 hover:text-amber-400" title="Reset password (send OTP)"><KeyRound className="w-4 h-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-rose-400" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-zinc-950 border-zinc-800">
                            <AlertDialogHeader><AlertDialogTitle>Delete {u.full_name}?</AlertDialogTitle>
                              <AlertDialogDescription>This will delete the user and all their submissions, revenue records, and copyright requests.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-rose-500 hover:bg-rose-600" onClick={() => del(u.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-zinc-500">No users found</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Add user dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Create an artist account. They can change the temporary password from Profile → Change Password after signing in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Row2 label="Full Name"><Input required data-testid="new-user-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-zinc-900 border-zinc-800" /></Row2>
            <Row2 label="Phone Number"><Input required data-testid="new-user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-900 border-zinc-800" /></Row2>
            <Row2 label="Email"><Input required type="email" data-testid="new-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-900 border-zinc-800" /></Row2>
            <Row2 label="Temporary Password"><Input required minLength={6} data-testid="new-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zinc-900 border-zinc-800" placeholder="min 6 chars — the user can change it after login" /></Row2>
            <Row2 label="Account Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="new-user-status" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Disabled">Disabled</SelectItem></SelectContent>
              </Select>
            </Row2>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setForm(empty); }} className="border-zinc-700 hover:bg-zinc-900">Cancel</Button>
            <Button onClick={create} data-testid="new-user-submit" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog — NO password field */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Passwords are never shown or set from here. Use the <span className="text-amber-400 font-medium">Reset Password</span> button to email the user a verification code instead.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Row2 label="Full Name"><Input value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} className="bg-zinc-900 border-zinc-800" /></Row2>
              <Row2 label="Phone"><Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="bg-zinc-900 border-zinc-800" /></Row2>
              <Row2 label="Status">
                <Select value={editing.status || "Active"} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="edit-user-status" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Disabled">Disabled</SelectItem></SelectContent>
                </Select>
              </Row2>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-zinc-700 hover:bg-zinc-900">Cancel</Button>
            <Button onClick={saveEdit} data-testid="edit-user-save" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserDetailDialog user={detail} onClose={() => setDetail(null)} onResetPassword={(u) => { setDetail(null); setResetting(u); }} />
      <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />
    </div>
  );
}

function Row2({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function UserDetailDialog({ user, onClose, onResetPassword }) {
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle>User details</DialogTitle>
          <DialogDescription className="text-zinc-500">
            We never store or display users' current passwords. Only the fact that a password is set is shown.
          </DialogDescription>
        </DialogHeader>
        {user && (
          <div className="space-y-3">
            <DetailRow k="Name" v={user.full_name} />
            <DetailRow k="Email" v={user.email} testid="detail-email" />
            <DetailRow k="Phone" v={user.phone} testid="detail-phone" />
            <DetailRow k="Account Status" v={user.status || "Active"} />
            <DetailRow k="Last Login" v={formatWhen(user.last_login_at)} testid="detail-last-login" />
            <DetailRow k="Password Status">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" data-testid="detail-password-status">
                <ShieldCheck className="w-3 h-3" /> Set
              </span>
            </DetailRow>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 hover:bg-zinc-900">Close</Button>
          {user && user.role !== "admin" && (
            <Button
              onClick={() => onResetPassword(user)}
              data-testid="detail-reset-password"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold"
            >
              <KeyRound className="w-4 h-4 mr-2" /> Reset Password
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ k, v, children, testid }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-center p-3 rounded-xl bg-zinc-900/60 border border-zinc-800">
      <div className="text-xs uppercase tracking-wider text-zinc-500">{k}</div>
      <div className="col-span-2 text-sm" data-testid={testid}>
        {children ?? v}
      </div>
    </div>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { if (user) { setSent(false); setBusy(false); } }, [user]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/send-reset-otp`);
      toast.success(data?.message || "Reset code sent");
      setSent(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send reset code");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {user && (
              <>Send a single-use, 10-minute verification code to <span className="text-cyan-400 font-mono">{user.email}</span>. The user can then set a new password from the <span className="text-cyan-400">Forgot Password</span> page. You will never see or know their password.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800 text-sm text-zinc-400">
          {sent
            ? "Code sent. The user should check their email — the code expires in 10 minutes and can be used only once. This action has been recorded in the password audit log."
            : "Confirm to send a reset code. This action will be logged in the password audit log with your admin identity."}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 hover:bg-zinc-900">
            {sent ? "Close" : "Cancel"}
          </Button>
          {!sent && (
            <Button
              onClick={send}
              disabled={busy}
              data-testid="reset-password-confirm"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold"
            >
              <KeyRound className="w-4 h-4 mr-2" />{busy ? "Sending..." : "Send reset code"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
